<template>
  <div class="mt-2 mb-2 chart-wrapper p-2">
    <h4 class="text-main text-center">
      Network Stats
    </h4>
    <div class="d-md-flex align-items-center">
      <b-dropdown
        variant="link"
        toggle-class="p-0"
        class="select-dropdown mr-1"
        no-caret
      >
        <template #button-content>
          <div class="d-flex align-items-center">
            <h5 class="heading text-heavy mb-0">
              {{
                selectedRgwLens.includes('bytesSent')
                  ? 'Bytes Sent & Received'
                  : 'Ops & Successful Ops'
              }}
            </h5>
            <GjIcon
              class="icon-right"
              name="ArrowDown"
              size="17"
            />
          </div>
        </template>
        <b-dropdown-item
          :active="selectedRgwLens.includes('bytesSent')"
          @click="selectRgwLens(0)"
        >
          Bytes Sent & Received
        </b-dropdown-item>
        <b-dropdown-item
          :active="selectedRgwLens.includes('ops')"
          @click="selectRgwLens(1)"
        >
          Ops & Successful Ops
        </b-dropdown-item>
      </b-dropdown>
      <v-select
        v-if="rgwCategoryOptions.length > 0"
        v-model="selectedRgwCategory"
        label="label"
        :options="rgwCategoryOptions"
        class="flex-fill rgw-select"
        placeholder="Select a category"
      />
    </div>
    <div class="mt-1 position-relative">
      <div
        v-if="gettingRgwCategories || gettingRgwLens"
        class="analytics-loading"
      >
        <b-spinner variant="primary" />
      </div>
      <vue-apex-charts
        v-else
        key="rgw-chart"
        type="line"
        :height="340"
        :options="rgwOptions"
        :series="rgwSeries"
      />
    </div>
  </div>
</template>
<script>
import VueApexCharts from 'vue-apexcharts'
import vSelect from 'vue-select'

export default {
  name: 'RgwChart',
  components: { VueApexCharts, vSelect },
  props: {
    interval: {
      type: Number,
      required: true,
    },
    bucket: {
      type: Object,
      required: true,
    },
    updateIndex: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      selectedRgwLens: ['bytesSent', 'bytesReceived'],
      selectedRgwCategory: null,
    }
  },
  computed: {
    rgwSeries() {
      const series = []
      this.selectedRgwLens.forEach(element => {
        series.push({
          data: this.rgwLensAnalytics.map(el => el[element]),
          name: this.formatCamelCaseString(element),
        })
      })
      return series
    },
    rgwOptions() {
      return {
        chart: {
          id: 'cc',
          zoom: { enabled: false },
          type: 'line',
          dropShadow: {
            enabled: true,
            top: 18,
            left: 2,
            blur: 5,
            opacity: 0.2,
          },
          offsetX: -10,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
        },
        stroke: {
          show: true,
          curve: 'smooth',
          width: 4,
        },
        legend: {
          show: false,
        },
        colors: ['#df87f2', '#2193b0'],
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            inverseColors: false,
            gradientToColors: ['#1d79f2', '#6dd5ed'],
            shadeIntensity: 1,
            type: 'horizontal',
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100, 100, 100],
          },
        },
        markers: {
          size: 0,
          hover: {
            size: 5,
          },
        },
        xaxis: {
          labels: {
            offsetY: 5,
            style: {
              fontSize: '0.857rem',
            },
            axisBorder: {
              show: false,
            },
            tickPlacement: 'on',
          },
          tickAmount: 5,
          categories: this.rgwLensAnalytics.map(el => this.findName(el.timestamp)),
        },
        yaxis: {
          labels: {
            minWidth: 40,
            style: {
              fontSize: '0.857rem',
            },
            formatter: val => (this.formatRgwLens
              ? this.formatBytes(Number(val), 2)
              : val.toLocaleString()),
          },
          min: 0,
          max: this.maxVal,
          tickAmount: 5,
        },
        tooltip: {
          followCursor: false,
        },
        noData: {
          text: 'No data available',
          align: 'center',
          verticalAlign: 'middle',
          offsetX: 0,
          offsetY: 0,
          style: {
            fontSize: '14px',
          },
        },
      }
    },
    formatRgwLens() {
      return (
        this.selectedRgwLens.includes('bytesSent')
        || this.selectedRgwLens.includes('bytesReceived')
      )
    },
    rgwLensAnalytics() {
      return this.$store.state.storageLensV2.rgwLens
    },
    rgwCategories() {
      return this.$store.state.storageLensV2.rgwCategories
    },
    rgwCategoryOptions() {
      return this.rgwCategories.map(el => ({
        value: el.name,
        label: el.name,
      }))
    },
    gettingRgwCategories() {
      return this.$store.state.storageLensV2.gettingRgwCategories
    },
    gettingRgwLens() {
      return this.$store.state.storageLensV2.gettingRgwLens
    },
    maxVal() {
      const firstEl = this.rgwLensAnalytics.length > 0
        ? this.rgwLensAnalytics.reduce((el, ele) => (el[this.selectedRgwLens[0]] > ele[this.selectedRgwLens[0]]
          ? el
          : ele))[this.selectedRgwLens[0]]
        : 0
      const secondEl = this.rgwLensAnalytics.length > 0
        ? this.rgwLensAnalytics.reduce((el, ele) => (el[this.selectedRgwLens[1]] > ele[this.selectedRgwLens[1]]
          ? el
          : ele))[this.selectedRgwLens[1]]
        : 0
      const value = firstEl > secondEl ? firstEl : secondEl
      return Math.floor(value * (1 + 20 / 100))
    },
  },
  watch: {
    selectedRgwCategory() {
      this.getRgwLens()
    },
    bucket() {
      this.getRgwLens()
    },
    interval() {
      this.getRgwLens()
    },
    updateIndex() {
      this.getRgwLens()
      this.getRgwCategories()
    },
  },
  created() {
    this.getRgwLens()
    this.getRgwCategories()
  },
  methods: {
    getRgwLens() {
      this.$store
        .dispatch('storageLensV2/getRgwLens', {
          period: this.interval,
          category: this.selectedRgwCategory?.value,
          bucket: this.bucket?.value,
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching rgw lens data.',
          )
        })
        .finally(() => {
          this.counter++
        })
    },
    getRgwCategories() {
      this.$store
        .dispatch('storageLensV2/getRgwCategories', {
          period: this.interval,
          category: this.selectedRgwCategory,
          bucket: this.bucket?.value,
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching rgw categories.',
          )
        })
    },
    selectRgwLens(val) {
      if (val === 0) {
        this.selectedRgwLens = ['bytesSent', 'bytesReceived']
        return
      }
      this.selectedRgwLens = ['ops', 'successfulOps']
    },
    findName(name) {
      const d = new Date(name)
      if (this.interval === 1) {
        return `${d.getHours()}:00`
      }
      const month = d.getMonth() + 1
      const days = d.getDate()
      return `${month < 10 ? `0${month}` : month}/${
        days < 10 ? `0${days}` : days
      }`
    },
  },
}
</script>
<style lang="scss" scoped>
.rgw-select {
  margin-top: 1rem;
  &::v-deep {
    .vs__selected-options {
      padding: 4px 2px;
    }
  }
  @media (min-width: 768px) {
    margin-top: 0;
    max-width: 300px;
    margin-left: auto;
  }
}
.select-dropdown::v-deep {
  .dropdown-item.active:hover {
    color: var(--primary);
  }
}

.analytics-loading {
  min-height: 355px;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
